import { useQueries } from '@tanstack/react-query';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import {
  DictionaryTypeCalendarConfigurationDto,
  DictionaryTypeDto,
  PermissionDto,
} from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';

type Return = {
  configurationByType: Partial<Record<DictionaryTypeDto, DictionaryTypeCalendarConfigurationDto>>;
  isError: boolean;
  isLoading: boolean;
};

/**
 * Calendar configurations of the given dictionary types. They are read with dictionary read; without
 * it nothing is asked for and every configuration stays unknown.
 */
export const useGetDictionaryCalendarConfigurations = (types: DictionaryTypeDto[]): Return => {
  const { dictionariesApi } = useApiClient();
  const canReadDictionaries = useHasPermission(PermissionDto.DictionaryRead);
  const uniqueTypes = canReadDictionaries ? Array.from(new Set(types)).sort() : [];
  const configurationQueries = useQueries({
    queries: uniqueTypes.map(dictionaryType => ({
      queryFn: async () =>
        (await dictionariesApi.getDictionaryTypeConfiguration(dictionaryType)).data,
      queryKey: [QueryKeyEnum.DICTIONARIES.CALENDAR, dictionaryType],
    })),
  });

  const configurationByType: Partial<
    Record<DictionaryTypeDto, DictionaryTypeCalendarConfigurationDto>
  > = {};
  configurationQueries.forEach(({ data: configuration }, index) => {
    const dictionaryType = uniqueTypes[index];
    if (configuration && dictionaryType) {
      configurationByType[dictionaryType] = configuration;
    }
  });

  return {
    configurationByType,
    isError: configurationQueries.some(query => query.isError),
    isLoading: configurationQueries.some(query => query.isLoading),
  };
};
